<template>
    <div class="w-full h-full flex flex-col p-6 lg:p-10 bg-gray-100">
        <div class="w-full h-full">
            <div class="w-full flex flex-col lg:flex-row justify-between items-center mb-10 lg:mb-5 gap-2">
                <h1 class="text-theme-primary text-3xl font-semibold">Balance Inquiry</h1>
                <div class="flex gap-2">
                    <div class="p-2 rounded-full bg-white hover:bg-gray-50 cursor-pointer" @click="refreshBalances">
                        <img src="@/assets/images/papsi_refetch.svg" alt="">
                    </div>
                    <button type="button" @click="toggleGlobalMask"
                        class="rounded-full items-center justify-center px-3 py-1 text-white text-xs lg:text-sm cursor-pointer"
                        :class="globalMask ? 'bg-primary' : 'bg-gray-700'">
                        {{ globalMask ? 'Show All' : 'Hide All' }}
                    </button>
                </div>
            </div>

            <!-- <SkeletonControl v-show="show_loader" class="mt-5" /> -->

            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div class="flex flex-col gap-4">
                    <div class="bg-white rounded-lg flex flex-col p-5 gap-4">
                        <div class="flex flex-row items-center gap-2 lg:gap-4">
                            <div class="flex items-center justify-center p-2 rounded-full bg-blue-50"><img
                                    src="@/assets/images/papsi_savings.svg" alt=""></div>
                            <p class="text-lg lg:text-xl text-blue-900 font-semibold">Savings</p>
                        </div>

                        <template v-for="entry in savingsEntries" :key="entry.maskKey">
                            <div class="rounded-lg bg-gray-100 w-full p-2 lg:p-5 flex items-center justify-between gap-5 transition"
                                :class="{
                                    'opacity-50 cursor-default': !$store.state.balances?.[entry.balanceKey]?.as_of,
                                    'cursor-pointer hover:bg-gray-200': $store.state.balances?.[entry.balanceKey]?.as_of
                                        }" @click="$store.state.balances?.[entry.balanceKey]?.as_of &&
                                        (entry.balanceKey === 'SSD'
                                    ? fetchSharesTransactionDetails(entry.balanceKey, entry.label)
                                    : fetchSavingsTransactionDetails(entry.balanceKey, entry.label)
                                )">
                                <p class="font-medium text-xs lg:text-base">{{ entry.label }}</p>
                                <div class="flex flex-col items-end justify-center gap-0 lg:gap-1" v-if="$store.state.balances?.[entry.balanceKey]?.as_of">
                                    <p class="text-base lg:text-xl font-bold flex flex-row items-center gap-1 lg:gap-2">
                                        <span class="flex flex-row">
                                            <PesoSignView />{{
                                                displayAmount($store.state.balances?.[entry.balanceKey]?.total_balance,
                                                    entry.maskKey) }}
                                        </span>
                                        <span class="cursor-pointer" @click.stop="toggleMask(entry.maskKey)">
                                            <img :class="{ 'opacity-60': isMasked(entry.maskKey) }" :src="isMasked(entry.maskKey)
                                                ? require('@/assets/images/papsi_password_invisible.svg')
                                                : require('@/assets/images/papsi_password_visible.svg')"
                                                class="w-4 h-4 lg:w-6 lg:h-6">
                                        </span>
                                    </p>
                                    <p class="italic text-xxs lg:text-sm text-gray-400">As of {{
                                        formatAsOfDate(
                                            $store.state.balances?.[entry.balanceKey]?.as_of
                                        )
                                    }}</p>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="bg-white rounded-lg flex flex-col p-5 gap-4">
                        <div class="flex flex-row items-center gap-2 lg:gap-4">
                            <div class="flex items-center justify-center p-2 rounded-full bg-yellow-50"><img
                                    src="@/assets/images/papsi_share_capital.svg" alt=""></div>
                            <p class="text-lg lg:text-xl text-yellow-900 font-semibold">Share Capital</p>
                        </div>

                        <template v-for="entry in shareCapitalEntries" :key="entry.maskKey">
                            <div
                                class="rounded-lg bg-gray-100 w-full p-2 lg:p-5 flex items-center justify-between gap-3 transition"
                                :class="{
                                    'opacity-50 cursor-default': !$store.state.balances?.[entry.balanceKey]?.as_of,
                                    'cursor-pointer hover:bg-gray-200': $store.state.balances?.[entry.balanceKey]?.as_of
                                }"
                                @click="$store.state.balances?.[entry.balanceKey]?.as_of &&
                                    fetchSavingsTransactionDetails(entry.balanceKey, entry.label)">
                                <p class="font-medium text-xs lg:text-base">{{ entry.label }}</p>
                                <div class="flex flex-col items-end justify-center gap-0 lg:gap-1" v-if="$store.state.balances?.[entry.balanceKey]?.as_of">
                                    <p class="text-base lg:text-xl font-bold flex flex-row items-center gap-1 lg:gap-2">
                                        <span class="flex flex-row">
                                            <PesoSignView />{{
                                                displayAmount($store.state.balances?.[entry.balanceKey]?.total_balance,
                                                    entry.maskKey) }}
                                        </span>
                                        <span class="cursor-pointer" @click.stop="toggleMask(entry.maskKey)">
                                            <img :class="{ 'opacity-60': isMasked(entry.maskKey) }" :src="isMasked(entry.maskKey)
                                                ? require('@/assets/images/papsi_password_invisible.svg')
                                                : require('@/assets/images/papsi_password_visible.svg')"
                                                class="w-4 h-4 lg:w-6 lg:h-6">
                                        </span>
                                    </p>
                                    <p class="italic text-xxs lg:text-sm text-gray-400">As of {{
                                        formatAsOfDate(
                                            $store.state.balances?.[entry.balanceKey]?.as_of
                                        )
                                    }}</p>
                                </div>
                            </div>
                        </template>
                    </div>
                </div>

                <div class="bg-white rounded-lg flex flex-col p-5 gap-4">
                    <div class="flex flex-row items-center gap-2 lg:gap-4">
                        <div class="flex items-center justify-center p-2 rounded-full bg-red-50"><img
                                src="@/assets/images/papsi_loans.svg" alt=""></div>
                        <p class="text-lg lg:text-xl text-red-900 font-semibold">Loans</p>
                    </div>

                    <template v-for="loan in loansToShow" :key="loan.maskKey">
                        <div
                            class="rounded-lg bg-gray-100 w-full p-2 lg:p-5 flex items-center justify-between transition gap-3"
                            :class="{
                                'opacity-50 cursor-default': $store.state.balances?.[loan.balanceKey]?.total_records == 0,
                                'cursor-pointer hover:bg-gray-200': $store.state.balances?.[loan.balanceKey]?.total_records > 0
                            }"
                            @click="$store.state.balances?.[loan.balanceKey]?.total_records > 0 &&
                                fetchLoansTransactionDetails(loan.balanceKey, loan.label)">
                            <p class="font-medium text-xs lg:text-base">{{ loan.label }}</p>
                            <div class="flex flex-col items-end justify-center gap-0 lg:gap-1"
                                v-if="!$store.state.balances?.[loan.balanceKey]?.total_records == 0">
                                <p class="text-base lg:text-xl font-bold flex flex-row items-center gap-1 lg:gap-2">
                                    <span class="flex flex-row">
                                        <PesoSignView />{{
                                            displayAmount($store.state.balances?.[loan.balanceKey]?.total_balance,
                                                loan.maskKey) }}
                                    </span>
                                    <span class="cursor-pointer" @click.stop="toggleMask(loan.maskKey)">
                                        <img :class="{ 'opacity-60': isMasked(loan.maskKey) }" :src="isMasked(loan.maskKey)
                                            ? require('@/assets/images/papsi_password_invisible.svg')
                                            : require('@/assets/images/papsi_password_visible.svg')"
                                            class="w-4 h-4 lg:w-6 lg:h-6">
                                    </span>
                                </p>
                                <p class="italic text-xxs lg:text-sm text-gray-400">As of {{
                                    formatAsOfDate(
                                        $store.state.balances?.[loan.balanceKey]?.as_of
                                    )
                                }}</p>
                            </div>
                        </div>
                    </template>

                    <div v-if="loanEntries.length > 5"
                        class="w-full text-center items-center justify-center flex flex-col mt-3">
                        <button type="button" @click="toggleLoanList" class="flex items-center gap-2 text-gray-500">
                            <img :class="{ 'rotate-180': showAllLoans }" src="@/assets/images/papsi_down copy.svg"
                                class="text-xxs lg:text-base w-6 h-6 lg:w-10 lg:h-10 bg-gray-50 hover:bg-gray-100 rounded-full transition-transform duration-200">
                            <span class="text-xs lg:text-base">{{ showAllLoans ? 'Show Less Loans' : 'Show More Loans'
                            }}</span>
                        </button>
                    </div>
                </div>
            </div>

            <ModalControl v-show="show_message_modal" v-bind:display="show_message_modal" :error="this.error"
                :success="this.success" :note="this.warning" :question="this.question" z_value="z-50"
                @positiveClick="handlePositiveClick()" :positiveButton="'OK'" :default_width="'max-w-xl'"
                id='vote_modal'>
                <div class="w-full px-5 pt-2 pb-4 flex flex-col text-xl justify-center text-center items-center">
                    {{ this.modal_message }}
                </div>
            </ModalControl>

        </div>
    </div>
</template>
<script>
import { defineComponent } from 'vue';
import ModalControl from '@/components/controls/ModalControl.vue';
import PesoSignView from '../static/PesoSignView.vue';
import User from "@/services/User";
import mixin from '@/mixin';

export default defineComponent({
    components: {
        ModalControl,
        PesoSignView
        // SkeletonControl
    },
    mixins: [mixin],
    inject: ['setLoading'],
    mounted() {
        let that = this;
        const state = that.$store.state;

        this.amountKeys.forEach(key => {
            this.maskStatus[key] = true;
        });

        if (!state.credentials.username) {
            that.getUserInfo();
        } else {
            that.checkUserFlow();
        }

        this.buildServiceCards();
    },
    data() {
        return {
            items: [],
            // show_loader: true,
            show_message_modal: false,
            modal_message: '',
            success: false,
            warning: false,
            error: false,
            question: false,
            expired: false,
            globalMask: true,
            maskStatus: {},
            showAllLoans: false,
            showTransactionModal: false,
            transactionData: [],
            selectedTransactionType: null,
            selectedTransactionLabel: '',
            transactionLoading: false,
            loanTypeLookup: {
                D06: 'Salary Loan',
                D07: 'Emergency Loan',
                D09: 'Calamity Loan',
                D14: 'Appliance Loan',
                D18: 'Special Educational Assistance Loan',
                D19: 'Housing Loan',
                D30: 'Business Loan',
                D36: 'Petty Cash Loan',
                D44: 'Back-To-Back Loan',
                D50: 'Commutation Loan',
                D52: 'Multi-Purpose Loan',
                D53: 'Car Loan',
                D54: 'Providential Loan',
            },
            date_today: new Date().toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                year: 'numeric',
            }),
        }
    },
    computed: {
        savingsEntries() {
            return [
                { label: 'Regular Savings Deposit', balanceKey: 'SAV', maskKey: 'SAV' },
                { label: 'Special Savings Deposit', balanceKey: 'SSD', maskKey: 'SSD' },
                // { label: 'Group Savings Deposit', balanceKey: 'SSD', maskKey: 'GROUP_SAVINGS' },
            ];
        },
        shareCapitalEntries() {
            return [
                { label: 'Share Capital', balanceKey: 'SHR', maskKey: 'SHR' },
            ];
        },
        loanEntries() {
            const loans = [
                { label: 'Salary Loan', balanceKey: 'D06', maskKey: 'D06' },
                { label: 'Emergency Loan', balanceKey: 'D07', maskKey: 'D07' },
                { label: 'Calamity Loan', balanceKey: 'D09', maskKey: 'D09' },
                { label: 'Appliance Loan', balanceKey: 'D14', maskKey: 'D14' },
                { label: 'Special Educational Assistance Loan', balanceKey: 'D18', maskKey: 'D18' },
                { label: 'Housing Loan', balanceKey: 'D19', maskKey: 'D19' },
                { label: 'Business Loan', balanceKey: 'D30', maskKey: 'D30' },
                { label: 'Petty Cash Loan', balanceKey: 'D36', maskKey: 'D36' },
                { label: 'Back-To-Back Loan', balanceKey: 'D44', maskKey: 'D44' },
                { label: 'Commutation Loan', balanceKey: 'D50', maskKey: 'D50' },
                { label: 'Multi-Purpose Loan', balanceKey: 'D52', maskKey: 'D52' },
                { label: 'Car Loan', balanceKey: 'D53', maskKey: 'D53' },
                { label: 'Providential Loan', balanceKey: 'D54', maskKey: 'D54' },
            ];

            return loans
                .filter((loan) => this.$store.state.balances?.[loan.balanceKey])
                .sort((a, b) => {
                    const aData = this.$store.state.balances?.[a.balanceKey] || {};
                    const bData = this.$store.state.balances?.[b.balanceKey] || {};

                    const aRecords = Number(aData.total_records || 0);
                    const bRecords = Number(bData.total_records || 0);

                    // Active loans first
                    if (aRecords > 0 && bRecords === 0) return -1;
                    if (aRecords === 0 && bRecords > 0) return 1;

                    // Both have records → sort by balance descending
                    if (aRecords > 0 && bRecords > 0) {
                        const aBalance = Number(String(aData.total_balance || 0).replace(/,/g, ''));
                        const bBalance = Number(String(bData.total_balance || 0).replace(/,/g, ''));
                        return bBalance - aBalance;
                    }

                    // Both have 0 records → keep D-code order
                    return a.balanceKey.localeCompare(b.balanceKey);
                })
        },
        loansToShow() {
            return this.showAllLoans ? this.loanEntries : this.loanEntries.slice(0, 3);
        },
        amountKeys() {
            return [
                ...this.savingsEntries,
                ...this.shareCapitalEntries,
                ...this.loanEntries,
            ].map((entry) => entry.maskKey);
        },
    },
    methods: {
        formatAsOfDate(date) {
            if (!date) {
                return 'N/A';
            }

            return new Date(date).toLocaleDateString('en-US', {
                month: 'long',
                day: '2-digit',
                year: 'numeric',
            });
        },
        formatLastSync(datetime) {
            if (!datetime) {
                return 'N/A';
            }

            // Convert "2026-07-31 16:26:28" to a format Date can reliably parse
            const date = new Date(datetime.replace(' ', 'T'));

            return date.toLocaleString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
            });
        },
        closed() {
            let that = this;

            that.$cookies.remove('user');
            that.$cookies.remove('user_session');
            that.$router.push('/');
            that.show_message_modal = false;
        },
        buildServiceCards() {
            this.items = [
                {
                    image: require('@/assets/images/papsi_eft.png'),
                    title: "Savings",
                    subtitle: "Receive your Patronage Refund directly to your Bank Account for FREE!",
                    open: () => this.$router.push('/services/EFT'),
                    deployed: true,
                },
                {
                    image: require('@/assets/images/papsi_calculator.png'),
                    title: "Share Capital",
                    subtitle: "Estimate your loan repayment quickly and easily.",
                    open: () => this.$router.push('/services/calculator'),
                    deployed: true,
                },
                {
                    image: require('@/assets/images/papsi_ledger.png'),
                    title: "Loans",
                    // subtitle: "View your current balance and account details anytime.",
                    open: () => this.$router.push('/services/balance-inquiry'),
                    deployed: true,
                },
            ];
        },
        toggleMask(key) {
            this.maskStatus[key] = !this.maskStatus[key];
        },
        toggleGlobalMask() {
            const nextMask = !this.globalMask;
            this.globalMask = nextMask;

            this.amountKeys.forEach(key => {
                this.maskStatus[key] = nextMask;
            });
        },
        toggleLoanList() {
            this.showAllLoans = !this.showAllLoans;
        },
        isMasked(key) {
            return this.maskStatus[key] !== false;
        },
        formatAmount(amount) {
            if (amount === null || amount === undefined || amount === '') {
                return '0.00';
            }
            const numeric = Number(String(amount).replace(/,/g, ''));
            if (Number.isNaN(numeric)) {
                return String(amount);
            }
            return numeric.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        },
        displayAmount(amount, key) {
            return this.isMasked(key) ? '*****' : this.formatAmount(amount);
        },
        refreshBalances() {
            this.setLoading(true);
            const state = this.$store.state;
            state.transaction_cache = {};
            state.transaction_data = [];
            state.transaction_label = '';
            state.transaction_balance = 0;
            state.transaction_key = '';
            state.transaction_date = '';
            state.last_sync = '';
            User.fetchBalances(this.$store.state.credentials.uuid)
                .then((response) => {
                    const data = response.data.data;
                    this.$store.state.balances = data;
                })
                .catch((error) => {
                    this.error = true;
                    this.show_message_modal = true;
                    this.modal_message = error.response?.data?.message || 'Failed to fetch balances';
                })
                .finally(() => {
                    this.setLoading(false);
                });
        },
        loadTransactions(type, label, route) {
            const state = this.$store.state;
            const cached = state.transaction_cache?.[type];

            if (cached) {
                state.transaction_data = cached.data;
                state.transaction_label = cached.label;
                state.transaction_balance = cached.balance;
                state.transaction_key = type;
                state.transaction_date = cached.date;
                this.$router.push(route);
                return;
            }

            if (!state.credentials.hash || !state.credentials.uuid) {
                this.error = true;
                this.show_message_modal = true;
                this.modal_message = 'Session information not available';
                return;
            }

            const selectedBalance = state.balances?.[type]?.total_balance ?? 0;
            const selectedDate = this.formatAsOfDate(state.balances?.[type]?.as_of ?? null);

            this.setLoading(true);
            User.fetchTransaction(
                state.credentials.hash,
                type,
                state.credentials.uuid,
                state.credentials.key
            ).then((response) => {
                const transactions = (response.data.data || []).map((transaction, index) => ({
                    id: index,
                    ...transaction,
                }));

                const lastTransaction = transactions.length > 0
                    ? transactions[transactions.length - 1]
                    : null;

                const latestBalance = lastTransaction?.balance ?? selectedBalance;
                const latestDate = lastTransaction?.date || state.balances?.[type]?.as_of;

                state.balances[type] = {
                    ...state.balances[type],
                    total_balance: latestBalance,
                    as_of: latestDate,
                };

                state.transaction_cache[type] = {
                    data: transactions,
                    label,
                    balance: latestBalance,
                    date: latestDate ? this.formatAsOfDate(latestDate) : selectedDate,
                };
                state.transaction_data = transactions;
                state.transaction_label = label;
                state.transaction_balance = latestBalance;
                state.transaction_key = type;
                state.transaction_date = latestDate ? this.formatAsOfDate(latestDate) : selectedDate;
                state.last_sync = this.formatLastSync(response.data.timestamp);
                this.$router.push(route);
            }).catch((error) => {
                if (error.response?.status === 403) {
                    this.expired = true;
                    this.modal_message = 'Your session has expired. Please log in again.';
                    state.logged = false;
                }
                this.error = true;
                this.show_message_modal = true;
                this.modal_message = error.response?.data?.message || 'Failed to fetch transactions';
            }).finally(() => {
                this.setLoading(false);
            });
        },
        fetchSavingsTransactionDetails(type, label) {
            this.loadTransactions(type, label, '/balance-inquiry/savings-balances');
        },
        fetchLoansTransactionDetails(type, label) {
            this.loadTransactions(type, label, '/balance-inquiry/loans-balances');
        },
        fetchSharesTransactionDetails(type, label) {
            this.loadTransactions(type, label, '/balance-inquiry/ssd-balances');
        },
        getUserInfo() {
            let that = this;
            const cookie = that.$cookies;
            const store = that.$store;
            let state = store.state;
            that.setLoading(true);

            User.recoverSession(cookie.get('user_session').hash).then(function (response) {
                let data = response.data.data;
                state.logged = true;
                state.credentials.first_name = data.info.first_name;
                state.credentials.last_name = data.info.last_name;
                state.credentials.middle_name = data.info.middle_name;
                state.credentials.regular = data.info.regular;
                state.credentials.express = data.express;
                state.credentials.pso_enroll = data.pso_enroll;
                state.credentials.pin_update = data.pin_update;
                state.credentials.membership_date = data.info.membership_date || null;
                state.credentials.status = data.status || null;
                state.credentials.account_no = data.info.account_number;
                state.gaUser = data.ga;
                state.credentials.email = data.info.email;
                state.credentials.gender = data.info.gender;
                state.credentials.birthdate = data.info.date_of_birth;
                state.credentials.age = data.info.age;
                state.credentials.membership_type = data.info.membership_type;
                state.credentials.registered_date = data.info.registered;
                state.credentials.key = data.key;
                state.credentials.mobile = data.info.mobile;
                state.credentials.name = data.info.first_name + " " + data.info.last_name + (data.info.suffix ? " " + data.info.suffix : "");
                state.credentials.suffix = data.info.suffix ?? '';
                state.credentials.qr = data.qr;
                state.credentials.uuid = data.uuid;
                state.credentials.serial_no = data.info.serial_number;
                state.credentials.timestamp = response.timestamp;
                state.credentials.username = data.info.username;
                state.credentials.hash = data.hash;
                state.credentials.has_pin = data.has_pin;
                state.credentials.deactivated = data.deactivated;
                state.credentials.tin = data.info.tin;
                state.balances = data.balances;
                const type2Config = data.config.find(item => item.type === 2);
                if (type2Config) {
                    state.credentials.config = { status: type2Config.status };
                } else {
                    state.credentials.config = null;
                }
                that.account_number = state.credentials.account_no;
                that.full_name = state.credentials.name;
                that.gender = state.credentials.gender;
                that.Astatus = state.credentials.status;
                that.Membership = state.credentials.membership_type;
                that.Member_since = state.credentials.membership_date;
                that.email = state.credentials.email;
                that.mobile = state.credentials.mobile;
                that.tin = state.credentials.tin;
                that.age = state.credentials.age;
                that.birthdate = state.credentials.birthdate;
                that.serial = state.credentials.serial_no == null ? '??????' : state.credentials.serial_no;
                that.username = state.credentials.username;
                that.registered = state.credentials.registered_date;
                that.qr = data.qr;
                that.currentTin = state.credentials.tin;
                that.express = data.express;
                that.deactivated = data.deactivated;
                const rawRegisteredDate = state.credentials.registered_date;
                if (rawRegisteredDate) {
                    const parts = rawRegisteredDate.split('-');
                    that.registered = `${parts[1]}/${parts[2]}/${parts[0]}`;
                }

                const rawBirthdate = state.credentials.birthdate;
                if (rawBirthdate) {
                    const parts = rawBirthdate.split('-');
                    that.birthdate = `${parts[1]}/${parts[2]}/${parts[0]}`;
                }

                that.checkUserFlow();
                that.setLoading(false);

            }).catch(function (error) {
                that.error = true;
                that.setLoading(false);
                that.show_message_modal = true;
                that.modal_message = error.response.data.message;
                if (error.response.status == 403) {
                    that.expired = true
                    that.modal_message = "Your session has expired. Please log in again.";
                    that.$store.state.logged = false;
                }
            });
        },
        checkUserFlow() {
            const that = this;
            const state = that.$store.state;
            const noContact = !state.credentials.mobile || !state.credentials.email;
            const config = state.credentials.config;
            const temporaryPIN = state.credentials.pin_update;
            const isValidated = that.$cookies.get('validated');

            // this.show_loader = true;
            // this.buildServiceCards();

            // if (this.$store.state.modules && this.$store.state.modules.length > 0) {
            //     this.buildServiceCards();
            //     return;
            // }


            if (state.credentials.deactivated) {
                this.$router.push('/user/settings');
                return;
            }

            if (noContact) {
                state.credentials.noContact = noContact;
                this.show_update = true;
                return;
            }

            if (state.credentials.enrolled_pin) {
                const hasValidConfig = config && config.status;
                const noContact = state.credentials.noContact;
                state.credentials.enrolled_pin = false;

                if (noContact) {
                    that.show_update = false;
                    that.show_pin_modal = false;
                    that.show_otp_modal = false;
                    that.$cookies.set('validated', true);
                } else {
                    if (hasValidConfig) {
                        that.requestOTP();
                    } else {
                        that.show_update = false;
                        that.show_pin_modal = false;
                        that.show_otp_modal = false;
                        that.$cookies.set('validated', true);
                    }
                }
                return;
            }


            if (!isValidated) {
                if (state.credentials.has_pin) {
                    this.show_pin_modal = true;
                } else {
                    this.$router.push('/user/settings');
                }
            }

            if (temporaryPIN) {
                this.$router.push('/user/settings');
                state.credentials.edit_temporary = true;
                return;
            }

            const missingInfo = !state.credentials.mobile || !state.credentials.email ||
                !state.credentials.tin || !state.credentials.birthdate;

            if (missingInfo && !that.$cookies.get('skipped')) {
                this.show_update = true;
            }
        },
        toTitleCase(str) {
            return str
                .toLowerCase()
                .replace(/\b\w/g, (char) => char.toUpperCase());
        },
        handlePositiveClick() {
            if (this.expired) {
                this.closed();
            } else {
                this.show_message_modal = false;
                this.show_pin_modal = false;
                this.pin_value = '';
            }
        },
    },
})
</script>
<style scoped>
.max-width-back {
    max-width: 50px;
}
</style>
